import React, { FC } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { AuthMiddleware } from "./auth/AuthProvider";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router";
import { useKeycloak } from "@react-keycloak/web";
import { TodoContextProvider } from "../../lib/src/todos/TodoContextProvider";
import { Planning } from "./pages/Planning";
import { Instellingen } from "./pages/Instellingen";
import { Personeel } from "./pages/Personeel";
import { PlannerContextProvider } from "./planning/PlannerContext";
import { APIContextProvider, useApi } from "./api/APIContext";
import { Holidays } from "./pages/Vrije dagen";
import { Teams } from "./pages/Teams";
import { Leveranciers } from "./pages/Leveranciers";
import initSentry from "./sentry";
import { Colors } from "./pages/Kleuren";
import { Orders } from "./pages/Orders";
import { Werkbonnen } from "./pages/Werkbonnen";
import { Reports } from "./pages/Reports";
import { ModalProvider } from "@traas/lib/src/components/layout/ModalProvider";
import { GlobalLoadingScreen } from "@traas/planning/src/components/layout/GlobalLoadingScreen";
import { navigation } from "./navigation";
import { Hours } from "./pages/Hours";
import { DateContextProvider } from "./planning/DateContext";
import { LiveLocation } from "./pages/LiveLocation";
import { APIProvider as GoogleMapsAPIProvider } from "@vis.gl/react-google-maps";
import config from "./config";
import { LiveLocationContextProvider } from "./realtime/LiveLocationContext";
import { TaskTypeDetails } from "./pages/TaskTypeDetails";
import { Werkbezoeken } from "./pages/Werkbezoeken";
import { WerkbezoekenDetails } from "./pages/WerkbezoekenDetails";
import Keycloak from "keycloak-js";
import { AuthProvider, generateDynamicColorSchemeCSS, getToken, useRoles } from "@ventumsoftware/react-toolkit";
import colors from "tailwindcss/colors";
import { RefreshController } from "../../lib/src/realtime/RefreshController";
import { PersoneelDetails } from "./pages/PersoneelDetails";
import { ApiResourceContextProvider } from "../../lib/src/api-resource-context/APIResourceContextProvider";
import { RealtimeContextProvider } from "@traas/lib/src/realtime/RealtimeConnectionContext";
import { RealtimeConnectionStatusBanner } from "@traas/lib/src/realtime/RealtimeConnectionStatusBanner";
import { RealtimeMessageHandler } from "@traas/planning/src/realtime/RealtimeMessageHandler";
import { OnlineEmployeesContextProvider } from "@traas/planning/src/realtime/OnlineEmployeesContext";

initSentry();

const keycloak = new Keycloak({
  url: config.keycloakUrl,
  realm: config.keycloakRealm,
  clientId: config.keycloakClientId,
});

// Mounted above AuthMiddleware (see RefreshController placement below), so it guards its own fetching
// against keycloak/role state instead of relying on AuthMiddleware's early-return.
const TodoContextProviderWithAuth: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { keycloak } = useKeycloak();
  const roles = useRoles();
  return (
    <TodoContextProvider
      config={config}
      tokenResolver={getToken}
      navigateFactory={useNavigate}
      enabled={keycloak.authenticated === true && roles.includes("office")}
    >
      {children}
    </TodoContextProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <>
    <style>{generateDynamicColorSchemeCSS(colors.blue)}</style>
    <AuthProvider keycloak={keycloak}>
      <BrowserRouter>
        <RefreshController>
          <TodoContextProviderWithAuth>
            <AuthMiddleware navigation={navigation}>
              <APIContextProvider>
                <RealtimeContextProvider config={config} tokenResolver={getToken}>
                  <LiveLocationContextProvider>
                    <ApiResourceContextProvider config={config} tokenResolver={getToken} navigateFactory={useNavigate}>
                      <ModalProvider>
                        <RealtimeConnectionStatusBanner />
                        <RealtimeMessageHandler />
                        <OnlineEmployeesContextProvider>
                          <GlobalLoadingScreen>
                            <DateContextProvider>
                              <PlannerContextProvider>
                                <GoogleMapsAPIProvider apiKey={config.googleApiKey}>
                                  <Routes>
                                    <Route path={"/"} element={<Planning />} />
                                    <Route path={"/bestellingen"} element={<Orders />} />
                                    <Route path={"/workorders"} element={<Werkbonnen />} />
                                    <Route path={"/werkbezoeken"} element={<Werkbezoeken />} />
                                    <Route path={"/werkbezoeken/:projectVisit"} element={<WerkbezoekenDetails />} />
                                    <Route path={"/tracking"} element={<LiveLocation />} />
                                    <Route path={"/personeel"} element={<Personeel />} />
                                    <Route path={"/personeel/:employee"} element={<PersoneelDetails />} />
                                    <Route path={"/teams"} element={<Teams />} />
                                    <Route path={"/holidays"} element={<Holidays />} />
                                    <Route path={"/reports"} element={<Reports />} />
                                    <Route path={"/instellingen"} element={<Instellingen />} />
                                    <Route path={"/leveranciers"} element={<Leveranciers />} />
                                    <Route path={"/kleuren"} element={<Colors />} />
                                    <Route path={"/kleuren/:taskType"} element={<TaskTypeDetails />} />
                                    <Route path={"/uren"} element={<Hours />} />
                                  </Routes>
                                </GoogleMapsAPIProvider>
                              </PlannerContextProvider>
                            </DateContextProvider>
                          </GlobalLoadingScreen>
                        </OnlineEmployeesContextProvider>
                      </ModalProvider>
                    </ApiResourceContextProvider>
                  </LiveLocationContextProvider>
                </RealtimeContextProvider>
              </APIContextProvider>
            </AuthMiddleware>
          </TodoContextProviderWithAuth>
        </RefreshController>
      </BrowserRouter>
    </AuthProvider>
    ,
  </>,
);
